import { useCallback, useMemo, type MouseEvent } from 'react';
import { useRouter } from 'next/router';
import ProductCard from '_components/ProductStrip/ProductCard';
import { isModifiedClick } from '_utils/GA4/clickTracking';
import { trackPlpCrossLinkingCardClick } from '_utils/GA4/trackPlpCrossLinkingEvent';
import { useHitSwatches } from '@hooks/useHitSwatches';
import { colourKeyForVariantSku } from '@utils/algolia/colourKeyForVariantSku';
import type { HitProps } from '@components/algolia/Hit';

export interface PlpCrossLinkingCardProps {
  /** The Algolia record resolved from the product code merch entered. */
  record: HitProps;
  /** Optional variant SKU that selects which colourway the card shows. */
  variantSku?: string | null;
  locale?: string;
  priority?: boolean;
  index: number;
  coOpBrand?: string | null;
}

/**
 * One curated product on the PLP cross-linking band.
 *
 * Price selection goes through the same `useHitSwatches` hook the grid's own
 * cards use, so customer-group and member pricing on this band always agrees
 * with the products rendered around it. Presentation is the shared `mini`
 * `ProductCard` on a white surface — the design has no reviews, swatches, tags,
 * or compare control, so none are passed.
 */
const PlpCrossLinkingCard = ({
  record,
  variantSku,
  locale,
  priority = false,
  index,
  coOpBrand,
}: PlpCrossLinkingCardProps) => {
  const { asPath } = useRouter();
  const initialColour = useMemo(
    () => colourKeyForVariantSku(record, variantSku),
    [record, variantSku]
  );

  const { activeVariant, priceGroupKey } = useHitSwatches(
    initialColour,
    record.variants,
    {
      price: record.price,
      salePrice: record.salePrice,
      image: record.image,
      imageTransparent: record.imageTransparent,
      badge: record.badge ?? null,
      badgeGuest: record.badgeGuest ?? null,
      sku: record.sku,
    },
    locale
  );

  // Mirrors `_components/Products/Hit`'s badge selection so the cross-linking
  // band's discount badge agrees with the main grid's cards, and comes from
  // the BC promotion badge rather than displayPrice/salePrice (ED-503): those
  // are formatted strings that can be a price *range* across sizes, and
  // parsing a range as a single number silently produces a bogus discount %.
  const rawBadge =
    record.isMemberPrice && priceGroupKey !== 'guest'
      ? activeVariant?.badge
      : activeVariant?.badgeGuest;

  const queryString = new URLSearchParams(
    activeVariant?.colourQuerySlug ?? ''
  ).toString();

  // `record.url` is already a complete, locale-correct path in the index.
  const productUrl = `${record.url}${queryString ? `?${queryString}` : ''}`;

  const handleClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      const data = {
        interactionLocation: asPath.split('?')[0],
        linkUrl: productUrl,
        linkText: record.title || '',
        coOpBrand: coOpBrand || null,
        index,
      };

      // A modified click keeps this page alive, so the hit is not at risk and
      // navigation must not be hijacked.
      if (isModifiedClick(e)) {
        trackPlpCrossLinkingCardClick({ data });
        return;
      }

      e.preventDefault();
      trackPlpCrossLinkingCardClick({
        data,
        eventCallback: () => {
          window.location.href = productUrl;
        },
      });
    },
    [asPath, productUrl, record.title, coOpBrand, index]
  );

  return (
    <ProductCard
      mini
      onTint
      priority={priority}
      productImage={
        activeVariant?.imageTransparent ||
        activeVariant?.image ||
        record.imageTransparent ||
        record.image ||
        '/images/pdp/product-placeholder.jpg'
      }
      brand={record.brand || ''}
      displayName={record.title || ''}
      productUrl={productUrl}
      displayPrice={
        activeVariant?.priceRange?.price || activeVariant?.priceFormatted || ''
      }
      salePrice={
        activeVariant?.priceRange?.salePrice ||
        activeVariant?.salePriceFormatted ||
        ''
      }
      isOnSaleOrClearance={Boolean(
        activeVariant?.salePriceFormatted ||
          activeVariant?.priceRange?.salePrice
      )}
      discountPercentage={rawBadge?.discount_percentage}
      multiDiscount={rawBadge?.multi_discount}
      memberBadgeCode={rawBadge?.is_member_price ? rawBadge?.code : undefined}
      productCode={record.parentSku}
      trackClick={handleClick}
    />
  );
};

export default PlpCrossLinkingCard;
